import React, { useEffect, useState, useRef, memo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
	PromoWrapper,
	getFormattedDesignData,
	PrintView,
	getDiscountDisplay
} from 'wsm-specials-display';
import { setClassNames } from 'ddc-classnames-js';
import {
	useSitemap,
	useLabels,
	useRequestData,
	usePrefs,
	useFlags
} from 'wsm-common-data';
import PromoTypeLayer from './PromoTypeLayer';
import DetailsButton from '../DetailsButton';
import CustomAssetPromo from './CustomAssetPromo';
import { getDestinationLink, getClickHandler } from '../../utilities/links';
import { shouldCustomAssetDisclaimerBtnBeOnTop } from '../../utilities/general';
import { formatIncentiveDate } from '../../utilities/incentive';
import { promoComponentPropTypes } from '../../utilities/commonPropTypes';
import {
	getCouponStatus,
	isProgramAspectRatioAccount,
	isMobile4x3
} from '../../utilities/aspect';
import { aR as ASPECT_RATIO } from '../../global-constants';
import { useURLPrefs } from '../../hooks/useURLPrefs';
import { ctaPrintFragment } from '../../templates/commonFragments';

const Promo = ({
	aspectRatio,
	promoIndex,
	isMobile = false,
	noNeedAriaCurrent = false,
	isVisible = true,
	positionLabel = null,
	promo,
	role = null,
	roleDescription = null,
	width,
	onPromoPushOutsideButton = () => {},
	isCurrent = false,
	vehicleBackgroundImage,
	modelOfferBackgroundImage
}) => {
	const labels = useLabels();
	const promoWrapper = useRef(null);
	const {
		enableCarousel,
		collection,
		aspectRatio: aspectRatioPref
	} = useURLPrefs();
	const sitemap = useSitemap();
	const { widgetName, windowId, locale } = useRequestData();
	const { reduceDollarSignSize } = usePrefs();
	const flags = useFlags();
	const { designs } = useSelector((state) => state.designs);
	const personalized = useSelector((state) => state.personalized);
	const account = useSelector((state) => state.account);
	const programAspectRatioAccount = isProgramAspectRatioAccount(account);
	// Feature flag to manage render aspect ratio
	// Ex:"Desktop10x1,Desktop4x1"
	const enableModuleSpecialsRendering =
		flags['enable-module-specials-rendering']?.split(',');

	const isSlideshow =
		aspectRatio === ASPECT_RATIO.DESKTOP_4X1 ||
		aspectRatio === ASPECT_RATIO.MOBILE_4X3_SLIDESHOW;
	const isDndStyleForBmw = isSlideshow && programAspectRatioAccount === 'bmw';
	const isDndStyleForMini =
		isSlideshow && programAspectRatioAccount === 'mini';
	const isCoupon = getCouponStatus(aspectRatio);
	const isCouponMobile = isCoupon && isMobile;
	const [disableLeadDialog, setDisableLeadDialog] = useState(false);
	const {
		autoGenerated = false,
		condition = 'NEW',
		customAssets = {},
		destinationLabel = '',
		destinationUrl = '',
		discountType = '',
		discountValue = null,
		id,
		incentiveData,
		inventoryCount = 0,
		oemId = null,
		title = '',
		type,
		incentiveId
	} = promo;

	const isCustomAsset = customAssets && customAssets[aspectRatio];
	const isMobile4x3Template = isMobile4x3(aspectRatio);
	const updatedAR = isMobile4x3Template
		? ASPECT_RATIO.MOBILE_4X3
		: aspectRatio;
	const promoType = isCustomAsset ? 'Asset' : type;
	const disclaimerBtnOnTop =
		shouldCustomAssetDisclaimerBtnBeOnTop(aspectRatio);
	const classes = [
		'promo',
		`promo-type-${type.toLowerCase()}`,
		`collection-${collection.toLowerCase()}`,
		isCustomAsset ? 'custom-asset' : 'non-custom-asset',
		isCouponMobile && enableCarousel === 'false' ? 'mb-4' : ''
	];
	const leadFormURL = sitemap.get('PROMOTIONS_LEAD_FORM_FRAGMENT');

	// Define state and component ref for Print feature
	const componentToPrintRef = useRef(null);
	const assetOutsiderPrintViewRef = useRef(null);
	// Handle destination link defaults
	const {
		url: ctaUrl,
		label: ctaLabel,
		isFullSlideLink
	} = getDestinationLink({
		condition,
		label: destinationLabel,
		url: destinationUrl,
		inventoryCount,
		isCustomAsset,
		labels,
		sitemap,
		type
	});

	const startingAtStyles = {
		display: 'flex',
		alignItems:
			aspectRatio === ASPECT_RATIO.DESKTOP_4X1 ? 'center' : 'flex-end',
		flexDirection:
			aspectRatio === ASPECT_RATIO.DESKTOP_4X1 ? 'row' : 'column',
		justifyContent: '',
		textAlign: 'end',
		labelStyles: {
			fontWeight: 'normal'
		}
	};

	// Create Discount display labels for FO promos
	const discountDisplay = getDiscountDisplay(
		locale,
		discountType,
		discountValue,
		labels,
		reduceDollarSignSize,
		startingAtStyles
	);
	let promoContent = <div className="hidden">Placeholder</div>;
	let addFullSlideCta = isFullSlideLink;
	let ctaAnchor;
	const isLeadCTA = ctaUrl === 'LEAD';
	const [customAssetDisclaimerBtn, setCustomAssetDisclaimerBtn] =
		useState(null);

	let promoIncentive;

	if (incentiveData) {
		promoIncentive = {
			...formatIncentiveDate(incentiveData, locale),
			incentiveId,
			error: null,
			condition: incentiveData?.applicableCondition,
			make: incentiveData?.applicableMake,
			specific: {
				_type: incentiveData?.incentive?.type,
				terms: incentiveData?.incentive?.aprTerms,
				bestTerm: incentiveData?.incentive?.bestAprTerm,
				...incentiveData?.incentive
			}
		};
	}

	if (isCustomAsset) {
		// Disclaimer styles and classes
		const disclaimerOnAspectRatioBoxClasses = [
			'asset-disclaimer',
			isDndStyleForBmw || isDndStyleForMini
				? 'absolute-top w-100 dnd-button-bmw-mini d-flex'
				: 'absolute-bottom',
			'absolute-left',
			'disclaimer-on-aspect-ratio-box'
		];
		const detailsButtonClasses = [
			disclaimerBtnOnTop && !isDndStyleForBmw && !isDndStyleForMini
				? 'text-black'
				: '',
			'btn-no-decoration',
			isDndStyleForBmw || isDndStyleForMini
				? 'fs-bolder w-100 text-white'
				: 'font-weight-normal',
			isDndStyleForBmw ? 'dnd-button-bmw-mini bg-dnd-bmw' : '',
			isDndStyleForMini
				? 'dnd-button-bmw-mini text-uppercase bg-dnd-mini'
				: ''
		];

		const disclaimerBtn = (
			<div className={setClassNames(disclaimerOnAspectRatioBoxClasses)}>
				<DetailsButton
					btnStyle={
						isDndStyleForBmw || isDndStyleForMini
							? 'bwm-mini'
							: 'link'
					}
					promoIndex={promoIndex}
					customAsset
					classes={[setClassNames(detailsButtonClasses)]}
					promo={promo}
					incentive={promoIncentive}
					fullLabel
					onShowLeadDialog={(el) => setDisableLeadDialog(el)}
					ctaPrint={ctaPrintFragment({
						promo,
						pushOutsiderCtaPrint: () =>
							assetOutsiderPrintViewRef?.current?.onPrintView()
					})}
				/>
			</div>
		);

		if (customAssetDisclaimerBtn === null)
			setCustomAssetDisclaimerBtn(disclaimerBtn);
	}

	useEffect(() => {
		if (
			isCurrent &&
			!disclaimerBtnOnTop &&
			customAssetDisclaimerBtn !== null &&
			updatedAR !== ASPECT_RATIO.DESKTOP_10X1
		) {
			onPromoPushOutsideButton(customAssetDisclaimerBtn);
		}
		return () => {
			onPromoPushOutsideButton(null);
		};
		/* eslint-disable react-hooks/exhaustive-deps */
	}, [isCurrent, disclaimerBtnOnTop, customAssetDisclaimerBtn]);

	useEffect(() => {
		let slideInScriptTimeout;
		// Refreshing our ui-dialog module
		if (typeof window !== 'undefined' && isVisible) {
			if (isMobile) {
				slideInScriptTimeout = setTimeout(
					() => window.DDC.mobile.slidein.init(),
					500
				);
			} else if (
				typeof window.jQuery === 'function' &&
				window?.DDC?.modules
			) {
				$(document).trigger('modulesRefresh', {
					modules: {
						dialog: window?.DDC?.modules?.dialog
					}
				});
			}
		}
		return () => {
			clearTimeout(slideInScriptTimeout);
		};
	}, [isMobile, isVisible]);

	if (!isVisible) {
		return null;
	}

	if (isCustomAsset) {
		// Render custom asset
		classes.push('slide-type-image');
		addFullSlideCta = !!ctaUrl;
		promoContent = (
			<CustomAssetPromo
				isMobile={isMobile}
				isVisible={isVisible}
				promo={promo}
				width={width}
			/>
		);
	} else {
		// Render HTML content
		classes.push('slide-type-html');

		promoContent = (
			<PromoTypeLayer
				promoIndex={promoIndex}
				ctaLabel={ctaLabel}
				ctaUrl={ctaUrl}
				discountDisplay={discountDisplay}
				isMobile={isMobile}
				isVisible={isVisible}
				promo={promo}
				incentive={promoIncentive}
				width={width}
				triggerPrint={() =>
					assetOutsiderPrintViewRef?.current?.onPrintView()
				}
				vehicleBackgroundImage={vehicleBackgroundImage}
				modelOfferBackgroundImage={modelOfferBackgroundImage}
			/>
		);
	}

	/* eslint-disable jsx-a11y/anchor-is-valid */
	if (addFullSlideCta) {
		const clickHandler = getClickHandler(promoType, {
			widgetName,
			windowId,
			promo,
			aspectRatio,
			personalized,
			promoIndex
		});
		const anchorIndex = isCoupon ? promoIndex * 10 : promoIndex;
		if (isLeadCTA) {
			ctaAnchor = (
				<a
					href="#"
					data-href={`${leadFormURL}?promotionId=${promo.id}`}
					tabIndex={!isVisible ? '-1' : anchorIndex}
					onClick={clickHandler}
					rel="noreferrer"
					className={disableLeadDialog ? '' : 'dialog'}
					data-width="375"
					data-title={promo.destinationLabel}
				>
					{promoContent}
				</a>
			);
		} else {
			ctaAnchor = (
				<>
					<a
						href={
							ctaUrl === 'NO_DESTINATION_URL' ? undefined : ctaUrl
						}
						target={
							promo?.ctaConfig?.openInNewTab ? '_blank' : '_self'
						}
						tabIndex={!isVisible ? '-1' : anchorIndex}
						onClick={clickHandler}
						rel="noreferrer"
					>
						{promoContent}
					</a>
				</>
			);
		}
	}

	// Wrap with anchor if the full slide is a link
	// Set tab index when slide is not being displayed so that keyboard users can't access it
	const wrappedContent = addFullSlideCta ? ctaAnchor : promoContent;

	// TODO: need to figure out whether this promo was optimized with EO
	// data-promo-eo={true}
	// window.DDC.siteSettings.isEo <- Checks for the DNA resource

	return enableModuleSpecialsRendering.includes(aspectRatio) ? (
		<div className={setClassNames(classes)}>
			<PromoWrapper
				promo={promo}
				account={account}
				personalized={false}
				aspectRatioProp={aspectRatioPref}
				designConfig={getFormattedDesignData(promo, designs)}
			/>
		</div>
	) : (
		<div
			className={setClassNames(classes)}
			data-promo-id={id}
			data-oem-id={oemId}
			data-title={!oemId ? title : null}
			// data-promo-eo={false}
			data-auto-generated={autoGenerated}
			data-url-href={addFullSlideCta ? ctaUrl : null}
			role={role}
			/* eslint-disable no-nested-ternary */
			aria-current={
				noNeedAriaCurrent
					? null
					: isCurrent || isVisible
					? 'true'
					: 'false'
			}
			aria-roledescription={roleDescription}
			aria-label={positionLabel}
			ref={promoWrapper}
		>
			{/* Render the disclaimer btn out of aspect ratio box
			for custom assets to not have 2 one unreachable anchor inside another */}
			{disclaimerBtnOnTop && customAssetDisclaimerBtn}
			{wrappedContent}
			{/* Print view template */}
			<div ref={componentToPrintRef}>
				<PrintView
					promoFromExternal={promo}
					ref={assetOutsiderPrintViewRef}
					componentToPrintRef={componentToPrintRef}
					isDisclosure={isDndStyleForBmw || isDndStyleForMini}
				/>
			</div>
		</div>
	);
};

Promo.propTypes = {
	aspectRatio: PropTypes.string.isRequired,
	promoIndex: PropTypes.number.isRequired,
	isMobile: PropTypes.bool,
	noNeedAriaCurrent: PropTypes.bool,
	isVisible: PropTypes.bool,
	isCurrent: PropTypes.bool,
	positionLabel: PropTypes.string,
	promo: PropTypes.shape(promoComponentPropTypes).isRequired,
	role: PropTypes.string,
	onPromoPushOutsideButton: PropTypes.func.isRequired,
	roleDescription: PropTypes.string,
	width: PropTypes.number.isRequired,
	vehicleBackgroundImage: PropTypes.string,
	modelOfferBackgroundImage: PropTypes.string
};

export default memo(Promo);
